/**
 * The cookie consent banner.
 *
 * Two purposes — analytics and marketing — so "accept"/"decline" here apply
 * to both at once. The sentence names the purposes and not the services behind
 * them; the cookie policy page (linked here) names those and is where either
 * purpose can be granted or revoked on its own (`legal.cookies.category*` in
 * `LegalPages.tsx`). A shared bulk choice with an equally-reachable granular
 * one next to it is the layered pattern regulators accept; a shared choice
 * with no way to split the two afterwards would not be.
 *
 * The bar is inverted (dark on a light page) because a bar in the page's own
 * colours was read as part of the page and passed over.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/link";
import { useT } from "@/i18n";
import { getConsent, onConsentChange, setConsent } from "@/state/cookie-consent";

const BUTTON = "border-transparent bg-background text-foreground hover:bg-background/90";

export function CookieBanner() {
  const t = useT();
  // Not read from storage while rendering: the prerendered page carries no banner, and the
  // first render in the browser has to match it to attach to it. Whether this visitor has
  // answered is known one effect later.
  const [consent, setLocalConsent] = useState<ReturnType<typeof getConsent>>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setLocalConsent(getConsent());
    // The build-time render is a browser under automation (as `Illustration` also relies
    // on); its snapshot is the page's HTML and must not carry a banner nobody can answer.
    setReady(!navigator.webdriver);
    return onConsentChange(setLocalConsent);
  }, []);

  if (!ready || consent !== null) return null;

  return (
    <div
      role="region"
      aria-label={t("cookieBanner.link")}
      className="fixed inset-x-0 bottom-0 z-50 bg-foreground text-background shadow-[0_-8px_30px_rgba(0,0,0,0.25)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <p className="text-sm leading-relaxed">
          {t("cookieBanner.body")}{" "}
          <Link to="/cookies" className="font-medium underline underline-offset-4 hover:opacity-80">
            {t("cookieBanner.link")}
          </Link>
        </p>
        {/* One look for both — a filled "accept" beside an outline "decline"
            nudges the choice, which regulators (CNIL among them) treat as a
            dark pattern: declining has to be exactly as easy as accepting. */}
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            size="sm"
            className={BUTTON}
            onClick={() => setConsent({ analytics: "declined", marketing: "declined" })}
          >
            {t("cookieBanner.declineAll")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={BUTTON}
            onClick={() => setConsent({ analytics: "accepted", marketing: "accepted" })}
          >
            {t("cookieBanner.acceptAll")}
          </Button>
        </div>
      </div>
    </div>
  );
}
